/**
 * Cap-height vs x-height scaling for OpenType paths (preview + export SVG).
 * Uppercase → first number = cap (H-line); lowercase-only → second = x-height;
 * digits-only → single number = cap target (numberSizeOnly sizes).
 */
import type { OtFont } from '../types/opentypeJsFont';

const NOMINAL_HEIGHT_CAP_MM: Record<string, number> = { '30mm': 30, '40mm': 40 };
const NOMINAL_HEIGHT_XHEIGHT_MM: Record<string, number> = { '30mm': 21, '40mm': 28 };

/** Preview scale: canvas px per mm. */
export const PREVIEW_SCALE = 1.5;

export type TextScalingMode = 'cap' | 'x-height';

/** Cap / number-only vs x-height. */
export function getTextScalingMode(text: string): TextScalingMode {
  const t = (text || '').trim();
  const hasLetter = /[a-zA-Z]/.test(t);
  const hasDigit = /\d/.test(t);
  if (hasDigit && !hasLetter) return 'cap';
  if (/[A-Z]/.test(t)) return 'cap';
  return 'x-height';
}

export function parseCapHeightMm(fontSize: string): number {
  const fromMap = NOMINAL_HEIGHT_CAP_MM[fontSize];
  if (fromMap !== undefined && fromMap !== null) return fromMap;
  const part = fontSize.split('/')[0].trim();
  const num = parseFloat(part);
  return Number.isFinite(num) ? num : 30;
}

export function parseXHeightMm(fontSize: string): number {
  const fromMap = NOMINAL_HEIGHT_XHEIGHT_MM[fontSize];
  if (fromMap !== undefined && fromMap !== null) return fromMap;
  const parts = fontSize.split('/').map((p) => p.trim());
  if (parts.length >= 2) {
    const num = parseFloat(parts[1]);
    if (Number.isFinite(num)) return num;
  } else if (parts.length === 1) {
    const num = parseFloat(parts[0]);
    if (Number.isFinite(num)) return num;
  } else {
    return 21;
  }
}

function measureBaselineToTop(font: OtFont, char: string, pathFontSize: number): number | null {
  try {
    // We generate the path with the baseline at 0.
    // In this coordinate system, anything above the baseline has a negative Y value.
    const path = font.getPath(char, 0, 0, pathFontSize);
    const box = path.getBoundingBox();

    // Check for empty glyphs
    if (box.x2 <= box.x1 || box.y2 <= box.y1) return null;

    // box.y1 is the topmost point. Since it's above the baseline (0),
    // it will be a negative number (e.g., -12).
    // We negate it to get the height (12).
    const distanceToTop = -box.y1;

    return distanceToTop > 0 ? distanceToTop : null;
  } catch {
    return null;
  }
}

type MetricsCache = {
  capMetricUnits: number | null;
  xMetricUnits: number | null;
};

const metricsCache = new WeakMap<OtFont, MetricsCache>();

function getCache(font: OtFont): MetricsCache {
  let c = metricsCache.get(font);
  if (!c) {
    c = { capMetricUnits: null, xMetricUnits: null };
    metricsCache.set(font, c);
  }
  return c;
}

function resolveCapMetricUnits(font: OtFont): number {
  const c = getCache(font);
  if (c.capMetricUnits !== null) return c.capMetricUnits;

  const u = font.unitsPerEm;

  const candidates = ['H', 'I', 'E'];
  const measures: number[] = [];

  for (const char of candidates) {
    const measured = measureBaselineToTop(font, char, u);
    if (measured !== null && measured > 0) {
      measures.push(measured);
    }
  }

  if (measures.length > 0) {
    return (c.capMetricUnits = Math.max(...measures));
  }

  // Fallback to the font's internal metadata if glyphs aren't available
  const sCap = (font as any).tables?.os2?.sCapHeight;
  if (sCap !== undefined && sCap !== null && sCap > 0) return (c.capMetricUnits = sCap);

  return (c.capMetricUnits = font.ascender);
}

function resolveXMetricUnits(font: OtFont): number {
  const c = getCache(font);
  if (c.xMetricUnits !== null) return c.xMetricUnits;

  const u = font.unitsPerEm;

  const candidates = ['x', 'z'];
  const measures: number[] = [];

  for (const char of candidates) {
    const measured = measureBaselineToTop(font, char, u);
    if (measured !== null && measured > 0) {
      measures.push(measured);
    }
  }

  if (measures.length > 0) {
    return (c.xMetricUnits = Math.max(...measures));
  }

  // Fallback 1: OS/2 Table
  const sxHeight = (font as any).tables?.os2?.sxHeight;
  if (sxHeight !== undefined && sxHeight !== null && sxHeight > 0) return (c.xMetricUnits = sxHeight);

  // Fallback 2: Geometric estimation (Standard x-height is ~70% of Cap Height)
  const cap = resolveCapMetricUnits(font);
  return (c.xMetricUnits = cap * 0.7);
}

/** OpenType path font size so drawn cap or x-height matches targetPx in preview space. */
export function computePathFontSize(font: OtFont, targetPx: number, mode: TextScalingMode): number {
  const u = font.unitsPerEm;
  if (mode === 'cap') {
    const metric = resolveCapMetricUnits(font);
    return (targetPx * u) / metric;
  }
  const metric = resolveXMetricUnits(font);
  return (targetPx * u) / metric;
}

/** Target size in preview px for a line (for PathElement.fontSize UI / fallbacks). */
export function getFontSizePx(text: string, fontSizeKey: string): number {
  const mode = getTextScalingMode(text);
  const mm = mode === 'cap' ? parseCapHeightMm(fontSizeKey) : parseXHeightMm(fontSizeKey);
  return mm * PREVIEW_SCALE;
}
